/** TODO temporary. will be refactored */

import { forkJoin, Observable, of, OperatorFunction, pipe } from 'rxjs';
import { catchError, map, mapTo, switchMap, tap } from 'rxjs/operators';

import { SecurityScope } from '../auth/auth.model';
import { AreaItem } from '../location/area.location.model';
import { LocationService } from '../location/location.service';
import { RegionItem } from '../location/region.location.model';
import { SiteItem } from '../location/site.location.model';
import { Dependencies } from '../store';

import { Column, Row } from './experimental.grid';
import { LobItem } from './lob.pi.model';
import { PeopleOfInterestUserService } from './people-of-interest.user.service';
import { ProfileUserService } from './profile.user.service';
import { RoleItem } from './role.user.model';
import { SearchUserConfigService } from './search-user.config.service';
import { ChainGroup } from './search-user.epic';
import { userSearchCriteriaFromCriteriaFormValueTransformer } from './search-user.form';
import {
    ColumnID,
    compare,
    StatusType,
    UserSearchResult,
    userSearchResultFromPersonOfInterestModelTransformer,
    userSearchResultFromUserHierarchyTransformer,
    userSearchResultFromUserTransformer,
} from './search-user.model';
import { SuperGroupItem } from './supergroup.pi.model';
import { TranslationKeyMap } from './user.i18n';
import { User, UserHierarchy } from './user.model';
import { UserService } from './user.service';

// TODO move to separate file
interface Lookups {
    lobs?: LobItem[];
    regions?: RegionItem[];
    areas?: AreaItem[];
    sites?: SiteItem[];
    superGroups?: SuperGroupItem[];
    roles?: RoleItem[];
}

export function listLookups(
    deps: Dependencies,
): OperatorFunction<any, Lookups> {
    const locationService = deps.injector.get<LocationService>(LocationService);
    const profileUserService =
        deps.injector.get<ProfileUserService>(ProfileUserService);
    const result: Lookups = {};
    return pipe(
        switchMap(() =>
            forkJoin([
                // eslint-disable-next-line no-return-assign
                profileUserService.listLobs().pipe(
                    catchError(() => of([])),
                    tap((ret) => (result.lobs = ret)),
                ),
                // eslint-disable-next-line no-return-assign
                locationService.listRegions().pipe(
                    catchError(() => of([])),
                    tap((ret) => (result.regions = ret)),
                ),
                // eslint-disable-next-line no-return-assign
                locationService.listAreas().pipe(
                    catchError(() => of([])),
                    tap((ret) => (result.areas = ret)),
                ),
                // eslint-disable-next-line no-return-assign
                locationService.listSites().pipe(
                    catchError(() => of([])),
                    tap((ret) => (result.sites = ret)),
                ),
                // eslint-disable-next-line no-return-assign
                profileUserService.listSuperGroups().pipe(
                    catchError(() => of([])),
                    tap((ret) => (result.superGroups = ret)),
                ),
                // eslint-disable-next-line no-return-assign
                profileUserService.listRoles().pipe(
                    catchError(() => of([])),
                    tap((ret) => (result.roles = ret)),
                ),
            ]),
        ),
        mapTo(result),
    );
}

export function retrieveUser<TPayload>(
    deps: Dependencies,
    grp: ChainGroup<TPayload>,
    userId: number,
): Observable<ChainGroup<TPayload>> {
    const service = deps.injector.get<UserService>(UserService);
    return of(null).pipe(
        switchMap(() => service.retrieve(userId)),
        catchError(() => of<User>(null)),
        map((user) => {
            grp.userProfile = user;
            grp.user = !user ? null : userSearchResultFromUserTransformer(user);
        }),
        mapTo(grp),
    );
}

export function retrieveManager<TPayload>(
    deps: Dependencies,
    grp: ChainGroup<TPayload>,
    userId: number,
): Observable<ChainGroup<TPayload>> {
    const service = deps.injector.get<UserService>(UserService);
    return of(null).pipe(
        switchMap(() => service.listHierarchy('managers', userId)),
        catchError(() => of<UserHierarchy[]>([])),
        map((users) => {
            if (!users.length) {
                return null;
            }
            // elaborate workaround
            const a = users[0];
            const b = new UserHierarchy();
            b.userId = a.managerId;
            a.merge(b);
            return userSearchResultFromUserHierarchyTransformer(a);
        }),
        // eslint-disable-next-line no-return-assign
        tap((user) => (grp.manager = user)),
        mapTo(grp),
    );
}

/// /////////////////

export function listTeam<TPayload>(
    deps: Dependencies,
    grp: ChainGroup<TPayload>,
    userId: number,
): Observable<ChainGroup<TPayload>> {
    const service = deps.injector.get<UserService>(UserService);
    return of(null).pipe(
        switchMap(() => service.listHierarchy('subordinatesV2', userId)),
        catchError(() => of<UserHierarchy[]>([])),
        map((users) =>
            users
                .filter((e) => !e.endDate)
                .map(userSearchResultFromUserHierarchyTransformer),
        ),
        tap((users) => {
            if (!grp.teamColumns) {
                grp.teamColumns = makeTeamColumns();
            }
            grp.teamRows = makeTeamRows(users);
        }),
        mapTo(grp),
    );
}

function makeTeamColumns(): Column[] {
    const width = 500;
    const height = 35;

    // TODO revise. consider moving references to TranslationKeyMap to presentation layer

    return [
        {
            id: ColumnID.NAME.toString(),
            width,
            height,
            compareFn: compare,
            ctx: TranslationKeyMap._53_NAME,
        },
    ];
}

function makeTeamRows(users: UserSearchResult[]): Row[] {
    const height = 30;

    return users.map((e) => ({
        id: `${e.userId}-${Math.random()}`,
        height,
        cells: [{ ctx: e, columnId: ColumnID.NAME.toString(), height }],
    }));
}

/// /////////////////

export function listPeopleOfInterest<TPayload>(
    deps: Dependencies,
    grp: ChainGroup<TPayload>,
    scope: SecurityScope,
): Observable<ChainGroup<TPayload>> {
    const service = deps.injector.get<PeopleOfInterestUserService>(
        PeopleOfInterestUserService,
    );
    const configService = deps.injector.get<SearchUserConfigService>(
        SearchUserConfigService,
    );
    return of(null).pipe(
        service.list(() => (scope != null ? scope : configService.scopeId)),
        map((users) =>
            users
                .filter((e) => e.isAvailable)
                .map(userSearchResultFromPersonOfInterestModelTransformer),
        ),
        tap((users) => {
            if (!grp.favoritesColumns) {
                grp.favoritesColumns = makeFavoriteUsersColumns();
            }
            grp.favoritesRows = makeFavoriteUsersRows(users);
        }),
        mapTo(grp),
    );
}

function makeFavoriteUsersColumns(): Column[] {
    const columns = makeTeamColumns();
    columns[0].width /= 2.25;
    return columns;
}

function makeFavoriteUsersRows(users: UserSearchResult[]): Row[] {
    return makeTeamRows(users);
}

export function togglePersonOfInterest<TPayload>(
    deps: Dependencies,
    grp: ChainGroup<TPayload>,
    user: UserSearchResult,
): OperatorFunction<any, ChainGroup<TPayload>> {
    const service = deps.injector.get<PeopleOfInterestUserService>(
        PeopleOfInterestUserService,
    );
    let ret: OperatorFunction<any, any>;
    if (user.favourite) {
        ret = pipe(service.delete(() => user.userId));
    } else {
        ret = pipe(service.create(() => user.userId));
    }
    return pipe(ret, mapTo(grp));
}

// TODO revise
export function markFavorites<TPayload>(
    grp: ChainGroup<TPayload>,
): OperatorFunction<any, ChainGroup<TPayload>> {
    return pipe(
        tap(() => {
            const found = (userId: number): boolean => {
                return (
                    grp.favoritesRows.findIndex(
                        (ee) =>
                            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                            ee.cells &&
                            ee.cells.length &&
                            ee.cells[0].ctx.userId === userId,
                    ) !== -1
                );
            };
            if (grp.user) {
                grp.user = {
                    ...grp.user,
                    favourite: found(grp.user.userId),
                };
            }
            grp.teamRows.forEach((e) => {
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                if (e.cells && e.cells.length) {
                    e.cells[0].ctx.favourite = found(e.cells[0].ctx.userId);
                }
            });
        }),
    );
}

/// /////////////////

export function search<TPayload>(
    deps: Dependencies,
    grp: ChainGroup<TPayload>,
    scope: SecurityScope,
): OperatorFunction<any, ChainGroup<TPayload>> {
    const userService = deps.injector.get<UserService>(UserService);
    const configService = deps.injector.get<SearchUserConfigService>(
        SearchUserConfigService,
    );
    return pipe(
        tap(() => {
            grp.searchFailed = false;
            grp.searchExceedsMaxCount = false;
        }),
        switchMap(() => {
            const criteria = userSearchCriteriaFromCriteriaFormValueTransformer(
                grp.criteria,
            );
            return userService.list2({
                ...criteria,
                scopeId: scope != null ? scope : configService.scopeId,
            });
        }),
        catchError(() => {
            grp.searchFailed = true;
            return of<UserSearchResult[]>([]);
        }),
        map((users) => {
            const roles = configService.excludedRoleIds;
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            if (roles && roles.length) {
                users = users.filter(
                    (user) => !roles.some((rol) => rol === user.roleId),
                );
            }
            grp.searchExceedsMaxCount =
                users.length > configService.userListMaxCount;
            return users.slice(
                0,
                Math.min(users.length, configService.userListMaxCount),
            );
        }),
        tap((users) => {
            if (!grp.searchColumns) {
                grp.searchColumns = makeSearchColumns();
            }
            grp.searchRows = makeSearchRows(users);
        }),
        mapTo(grp),
    );
}

function makeSearchColumns(): Column[] {
    const width = 158;
    const height = 35;

    // TODO revise. consider moving references to TranslationKeyMap to presentation layer

    return [
        {
            id: ColumnID.NAME.toString(),
            width,
            height,
            compareFn: compare,
            ctx: TranslationKeyMap._53_NAME,
        },
        {
            id: ColumnID.SITE.toString(),
            width,
            height,
            compareFn: compare,
            ctx: TranslationKeyMap._64_SITE,
        },
        {
            id: ColumnID.ROLE.toString(),
            width,
            height,
            compareFn: compare,
            ctx: TranslationKeyMap._59_ROLE,
        },
        {
            id: ColumnID.SUPER_GROUP.toString(),
            width,
            height,
            compareFn: compare,
            ctx: TranslationKeyMap._67_SUPERGROUP,
        },
        {
            id: ColumnID.MANGER_NAME.toString(),
            width,
            height,
            compareFn: compare,
            ctx: TranslationKeyMap._50_MANAGER_NAME,
        },
        {
            id: ColumnID.STATUS.toString(),
            width,
            height,
            hidden: true,
            compareFn: compare,
            ctx: TranslationKeyMap._127_STATUS,
        },
    ];
}

function makeSearchRows(users: UserSearchResult[]): Row[] {
    const height = 25;

    return users.map((e) => ({
        id: `${e.userId}-${Math.random()}`,
        height,
        cells: [
            { ctx: e, columnId: ColumnID.NAME.toString(), height },
            { ctx: e, columnId: ColumnID.SITE.toString(), height },
            { ctx: e, columnId: ColumnID.ROLE.toString(), height },
            { ctx: e, columnId: ColumnID.SUPER_GROUP.toString(), height },
            { ctx: e, columnId: ColumnID.MANGER_NAME.toString(), height },
            { ctx: e, columnId: ColumnID.STATUS.toString(), height },
        ],
    }));
}

export function statusColumnCheck(
    columns: Column[],
    statusType: StatusType,
): boolean {
    if (!columns) {
        return;
    }
    const c = columns.find((e) => e.id === ColumnID.STATUS.toString());
    let didChange: boolean;
    if (c) {
        const prev = c.hidden;
        c.hidden = statusType !== StatusType.BOTH;
        didChange = prev !== c.hidden;
    }
    return didChange;
}
